import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  MoreHorizontal,
  Plus,
  Sparkles,
  Lock,
  Globe,
  Upload,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useBoundStore } from "../../store";
import { useI18n } from "../../i18n";
import { Playbook } from "../../services/Playbook";
import CreatePlaybookDialog from "./CreatePlaybookDialog";
import CreatePlaybookAIDialog from "./CreatePlaybookAIDialog";
import ImportPlaybookDialog from "./ImportPlaybookDialog";
import WorkflowsTab from "./WorkflowsTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function PlaybooksPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const tab = requestedTab === "workflows" ? requestedTab : "playbooks";
  // Standard agreements moved to the Compare page
  useEffect(() => {
    if (requestedTab === "standard-agreements")
      navigate("/compare?view=standards", { replace: true });
  }, [requestedTab, navigate]);
  const selectTab = (value: string) =>
    setSearchParams(value === "playbooks" ? {} : { tab: value }, {
      replace: true,
    });
  const getUser = useBoundStore((state) => state.getUser);
  const user = getUser();

  // Zustand store
  const playbooks = useBoundStore((state) => state.playbooks);
  const isLoading = useBoundStore((state) => state.isLoading);
  const fetchPlaybooks = useBoundStore((state) => state.fetchPlaybooks);
  const deletePlaybook = useBoundStore((state) => state.deletePlaybook);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createAIDialogOpen, setCreateAIDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playbookToDelete, setPlaybookToDelete] = useState<Playbook | null>(null);

  // Links from the chat ("new playbook", "upload playbook") open the dialog here
  const requestedDialog = searchParams.get("new");
  useEffect(() => {
    if (requestedDialog !== "create" && requestedDialog !== "import") return;
    if (requestedDialog === "create") setCreateDialogOpen(true);
    else setImportDialogOpen(true);
    setSearchParams({}, { replace: true });
  }, [requestedDialog, setSearchParams]);
  // Table states
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Fetch playbooks on mount
  useEffect(() => {
    const loadPlaybooks = async () => {
      try {
        console.log("Fetching playbooks...");
        await fetchPlaybooks();
        console.log("Playbooks fetched successfully");
      } catch (error) {
        console.error("Error fetching playbooks:", error);
        toast.error(t.playbooks.page.loadFailed);
      }
    };
    loadPlaybooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate playbooks into private and organization
  const privatePlaybooks = useMemo(
    () => playbooks.filter((p) => p.isPrivate && p.userId === user?.id),
    [playbooks, user?.id]
  );
  const organizationPlaybooks = useMemo(
    () => playbooks.filter((p) => !p.isPrivate),
    [playbooks]
  );

  // Handle delete
  const handleOpenDeleteDialog = useCallback((playbook: Playbook) => {
    setPlaybookToDelete(playbook);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setPlaybookToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!playbookToDelete) return;

    const success = await deletePlaybook(playbookToDelete.id);
    if (success) {
      toast.success(t.playbooks.page.deleteSuccess);
    } else {
      toast.error(t.playbooks.page.deleteFailed);
    }
    handleCloseDeleteDialog();
  }, [playbookToDelete, deletePlaybook, handleCloseDeleteDialog, t]);

  // Handle view
  const handleView = useCallback(
    (playbookId: string) => {
      navigate(`/playbooks/${playbookId}`);
    },
    [navigate]
  );

  // Column definitions
  const columns: ColumnDef<Playbook>[] = useMemo(() => [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 hover:bg-transparent"
        >
          {t.playbooks.table.name}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2 font-medium">
          <span>{row.getValue("name")}</span>
          {row.original.isApproved && (
            <Badge
              variant="outline"
              className="gap-1 border-brand/40 text-brand"
            >
              <BadgeCheck className="h-3 w-3" />
              {t.playbooks.table.approvedBadge}
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: "agreementType",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 hover:bg-transparent"
        >
          {t.playbooks.table.agreementType}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div>{row.getValue("agreementType")}</div>,
    },
    {
      accessorKey: "rules",
      header: t.playbooks.table.rules,
      cell: ({ row }) => {
        const rules = row.original.rules || [];
        const activeRules = rules.filter((r) => r.isActive);
        return (
          <Badge variant="secondary">
            {t.playbooks.table.activeBadge(activeRules.length, rules.length)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "isPrivate",
      header: t.playbooks.table.visibility,
      cell: ({ row }) => {
        const isPrivate = row.getValue("isPrivate");
        return isPrivate ? (
          <Badge variant="outline" className="gap-1">
            <Lock className="h-3 w-3" />
            {t.playbooks.table.private}
          </Badge>
        ) : (
          <Badge variant="outline" className="gap-1">
            <Globe className="h-3 w-3" />
            {t.playbooks.table.organization}
          </Badge>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 hover:bg-transparent"
        >
          {t.playbooks.table.created}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue("createdAt"));
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return <div>{`${day}-${month}-${year}`}</div>;
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const playbook = row.original;
        const canEdit = playbook.userId === user?.id;

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">{t.playbooks.table.openMenu}</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t.playbooks.table.actions}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleView(playbook.id)}>{t.playbooks.table.viewDetails}</DropdownMenuItem>
                <DropdownMenuSeparator />
                {canEdit && (
                  <DropdownMenuItem onClick={() => handleOpenDeleteDialog(playbook)} className="text-red-600">
                    {t.playbooks.table.delete}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ], [handleView, handleOpenDeleteDialog, user?.id, t]);

  // Private playbooks table
  const privateTable = useReactTable({
    data: privatePlaybooks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  // Organization playbooks table
  const orgTable = useReactTable({
    data: organizationPlaybooks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  // Show loading state
  if (isLoading && playbooks.length === 0) {
    return (
      <div className="w-full space-y-4 p-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-tour="playbooks-title" className="text-3xl font-bold tracking-tight">{t.playbooks.page.title}</h1>
          <p className="text-muted-foreground">
            {t.playbooks.page.subtitle}
          </p>
        </div>
        {tab === "playbooks" && (
        <div className="flex gap-2">
          <Button
            data-tour="playbooks-create"
            onClick={() => {
              console.log('Create button clicked');
              setCreateDialogOpen(true);
            }}
            variant="outline"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t.playbooks.page.createPlaybook}
          </Button>
          <Button
            data-tour="playbooks-import"
            onClick={() => {
              console.log('Import button clicked');
              setImportDialogOpen(true);
            }}
            variant="outline"
          >
            <Upload className="mr-2 h-4 w-4" />
            {t.playbooks.page.import}
          </Button>
          <Button
            data-tour="playbooks-create-ai"
            onClick={() => {
              console.log('Create AI button clicked');
              setCreateAIDialogOpen(true);
            }}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {t.playbooks.page.createWithAI}
          </Button>
        </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={selectTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="playbooks">{t.playbooks.tabs.playbooks}</TabsTrigger>
          <TabsTrigger value="workflows">{t.playbooks.tabs.workflows}</TabsTrigger>
        </TabsList>
        <TabsContent value="playbooks" className="space-y-4">
      {/* Private Playbooks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {t.playbooks.sections.privateTitle}
          </CardTitle>
          <CardDescription>{t.playbooks.sections.privateDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder={t.playbooks.table.filterPlaceholder}
              value={(privateTable.getColumn("name")?.getFilterValue() as string) ?? ""}
              onChange={(event) => privateTable.getColumn("name")?.setFilterValue(event.target.value)}
              className="max-w-sm"
            />
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {privateTable.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {privateTable.getRowModel().rows?.length ? (
                  privateTable.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      onClick={() => handleView(row.original.id)}
                      className="cursor-pointer"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      {isLoading ? t.playbooks.table.loading : t.playbooks.table.noPrivate}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end space-x-2 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => privateTable.previousPage()}
              disabled={!privateTable.getCanPreviousPage()}
            >
              {t.playbooks.table.previous}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => privateTable.nextPage()}
              disabled={!privateTable.getCanNextPage()}
            >
              {t.playbooks.table.next}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Organization Playbooks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {t.playbooks.sections.organizationTitle}
          </CardTitle>
          <CardDescription>{t.playbooks.sections.organizationDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder={t.playbooks.table.filterPlaceholder}
              value={(orgTable.getColumn("name")?.getFilterValue() as string) ?? ""}
              onChange={(event) => orgTable.getColumn("name")?.setFilterValue(event.target.value)}
              className="max-w-sm"
            />
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {orgTable.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {orgTable.getRowModel().rows?.length ? (
                  orgTable.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      onClick={() => handleView(row.original.id)}
                      className="cursor-pointer"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      {isLoading ? t.playbooks.table.loading : t.playbooks.table.noOrganization}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end space-x-2 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => orgTable.previousPage()}
              disabled={!orgTable.getCanPreviousPage()}
            >
              {t.playbooks.table.previous}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => orgTable.nextPage()}
              disabled={!orgTable.getCanNextPage()}
            >
              {t.playbooks.table.next}
            </Button>
          </div>
        </CardContent>
      </Card>

        </TabsContent>
        <TabsContent value="workflows">
          <WorkflowsTab />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {createDialogOpen && <CreatePlaybookDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />}
      {importDialogOpen && <ImportPlaybookDialog open={importDialogOpen} onOpenChange={setImportDialogOpen} />}
      {createAIDialogOpen && <CreatePlaybookAIDialog open={createAIDialogOpen} onOpenChange={setCreateAIDialogOpen} />}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.playbooks.deleteDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.playbooks.deleteDialog.description(playbookToDelete?.name ?? "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDeleteDialog}>{t.playbooks.deleteDialog.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t.playbooks.deleteDialog.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
